import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, TrendingUp, TrendingDown, Building2, Globe, ExternalLink, Bookmark,
  DollarSign, BarChart3, PieChart, Target, Activity, Award, ArrowRight, ChevronDown
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getStockSummary, getStockChart, getStockPeers, getStockHeadlines, getWatchlist } from '../../services/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, ComposedChart
} from 'recharts';
import toast from 'react-hot-toast';
import WhatsAppSendButton from '../components/WhatsAppSendButton';
// @ts-ignore
import Spinner from '../../components/Spinner';

interface StockData {
  ticker: string;
  companyName: string;
  shortName?: string;
  sector?: string;
  industry?: string;
  website?: string;
  business_summary?: string;
  price?: number;
  change?: number;
  changePercent?: number;
  dayLow?: number;
  dayHigh?: number;
  fiftyTwoWeekLow?: number;
  fiftyTwoWeekHigh?: number;
  previousClose?: number;
  marketCap?: number;
  enterpriseValue?: number;
  peRatio?: number;
  forwardPE?: number;
  pegRatio?: number;
  priceToBook?: number;
  priceToSales?: number;
  evToRevenue?: number;
  evToEbitda?: number;
  profitMargin?: number;
  operatingMargin?: number;
  returnOnAssets?: number;
  returnOnEquity?: number;
  revenueGrowth?: number;
  earningsGrowth?: number;
  totalRevenue?: number;
  revenuePerShare?: number;
  earningsPerShare?: number;
  dividendYield?: number;
  dividendRate?: number;
  payoutRatio?: number;
  volume?: number;
  averageVolume?: number;
  beta?: number;
  targetMeanPrice?: number;
  targetHighPrice?: number;
  targetLowPrice?: number;
  recommendationKey?: string;
  numberOfAnalystOpinions?: number;
  totalCash?: number;
  totalDebt?: number;
  debtToEquity?: number;
  currentRatio?: number;
  ai_business_summary?: string;
  bullish_points?: string[];
  bearish_points?: string[];
  [key: string]: any;
}

type Tab = 'overview' | 'financials' | 'analyst';

// Format helpers
const fmt = (num: number | undefined, decimals = 2): string => {
  if (num === undefined || num === null) return '—';
  return num.toFixed(decimals);
};
const fmtPct = (num: number | undefined): string => {
  if (num === undefined || num === null) return '—';
  return `${(num * 100).toFixed(2)}%`;
};
const fmtLarge = (num: number | undefined): string => {
  if (!num) return '—';
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return `$${num.toLocaleString()}`;
};
const fmtPrice = (num: number | undefined): string =>
  num !== undefined ? `$${num.toFixed(2)}` : '—';

// Compact stat row for financials
const StatRow: React.FC<{ label: string; value: string; note?: string; positive?: boolean; negative?: boolean }> =
  ({ label, value, note, positive, negative }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <div className="text-right">
      <span className={`text-sm font-semibold ${positive ? 'text-emerald-600' : negative ? 'text-red-600' : 'text-slate-800'}`}>
        {value}
      </span>
      {note && <span className="ml-2 text-xs text-slate-400">{note}</span>}
    </div>
  </div>
);

// Section header inside tabs
const SectionHead: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
    <span className="text-slate-400">{icon}</span>
    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</h4>
  </div>
);

// Analyst rating pill
const ratingColor = (key: string | undefined) => {
  if (!key) return 'bg-slate-100 text-slate-600';
  const k = key.toLowerCase();
  if (k.includes('buy') || k === 'strong buy') return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
  if (k.includes('hold') || k === 'neutral') return 'bg-amber-100 text-amber-700 border border-amber-200';
  if (k.includes('sell') || k.includes('underperform')) return 'bg-red-100 text-red-700 border border-red-200';
  return 'bg-slate-100 text-slate-600';
};

const StockAnalysisView: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchTicker, setSearchTicker] = useState('');
  const [stockData, setStockData] = useState<StockData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [watchlistTickers, setWatchlistTickers] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartPeriod, setChartPeriod] = useState('1Y');
  const [peers, setPeers] = useState<any[]>([]);
  const [headlines, setHeadlines] = useState<any[]>([]);

  const fetchWatchlist = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const data = await getWatchlist(token);
      const tickers = (data.watchlist || []).map((item: any) => item.ticker);
      setWatchlistTickers(tickers.slice(0, 12));
    } catch { /* silently fail */ }
  }, [currentUser]);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  // Check for pending ticker from insight view (cross-view navigation)
  useEffect(() => {
    const pending = localStorage.getItem('aiq_navigate_ticker');
    if (pending) {
      localStorage.removeItem('aiq_navigate_ticker');
      setSearchTicker(pending);
      // Delay slightly to ensure component is fully mounted
      setTimeout(() => {
        loadStockData(pending);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadChartData = async (ticker: string, period: string) => {
    try {
      const data = await getStockChart(ticker, period);
      setChartData(data.chart || []);
    } catch {
      setChartData([]);
    }
  };

  const loadPeers = async (ticker: string) => {
    try {
      const data = await getStockPeers(ticker);
      setPeers(data.peers || []);
    } catch {
      setPeers([]);
    }
  };

  const loadHeadlines = async (ticker: string) => {
    try {
      const data = await getStockHeadlines(ticker);
      setHeadlines(data.headlines || []);
    } catch {
      setHeadlines([]);
    }
  };

  const loadStockData = async (ticker: string) => {
    if (!ticker) return;
    setIsLoading(true);
    setActiveTab('overview');
    try {
      const summaryData = await getStockSummary(ticker);
      setStockData(summaryData);
      // Load chart, peers, and news in parallel
      loadChartData(ticker, chartPeriod);
      loadPeers(ticker);
      loadHeadlines(ticker);
    } catch {
      toast.error(`Failed to load data for ${ticker.toUpperCase()}`);
      setStockData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTicker.trim()) return;
    loadStockData(searchTicker.trim());
  };

  // Upside % from current price to analyst target
  const upsidePct = stockData?.targetMeanPrice && stockData?.price
    ? ((stockData.targetMeanPrice - stockData.price) / stockData.price) * 100
    : null;

  return (
    <div className="h-full overflow-y-auto bg-slate-50">

      {/* ── Search bar ───────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 py-3 shadow-sm">
        <form onSubmit={handleSearch} className="flex gap-2 items-center">
          <div className="flex items-center gap-2 text-slate-400 mr-1">
            <Search size={18} />
          </div>
          <input
            type="text"
            value={searchTicker}
            onChange={(e) => setSearchTicker(e.target.value.toUpperCase())}
            placeholder="Enter ticker symbol — AAPL, TSLA, MSFT…"
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400 text-slate-800 uppercase font-medium"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading || !searchTicker.trim()}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold disabled:opacity-40 hover:bg-blue-700 transition-colors"
          >
            Analyze
          </button>
        </form>

        {/* Watchlist quick-picks */}
        {watchlistTickers.length > 0 && (
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Bookmark size={12} className="text-slate-400 shrink-0" />
            {watchlistTickers.map(t => (
              <button
                key={t}
                onClick={() => { setSearchTicker(t); loadStockData(t); }}
                className="px-2.5 py-1 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded text-xs font-medium text-slate-600 hover:text-blue-600 transition-all"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Loading ──────────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex items-center justify-center py-24">
          <Spinner />
        </div>
      )}

      {/* ── Stock Details ─────────────────────────────────────────── */}
      {!isLoading && stockData && (
        <div>
          {/* Header banner */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 px-8 py-6 text-white">
            <div className="flex items-start gap-5">
              {/* Ticker initial */}
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center border border-white/20 shrink-0">
                <span className="text-2xl font-black">{stockData.ticker[0]}</span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-3xl font-extrabold tracking-tight">{stockData.ticker}</h1>
                  {stockData.sector && (
                    <span className="bg-white/10 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border border-white/20">
                      {stockData.sector}
                    </span>
                  )}
                  {stockData.recommendationKey && (
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${ratingColor(stockData.recommendationKey)}`}>
                      {stockData.recommendationKey.replace('_', ' ')}
                    </span>
                  )}
                </div>
                <p className="text-base text-white/80 mb-3">{stockData.companyName}</p>

                <div className="flex items-baseline flex-wrap gap-4">
                  {stockData.price && (
                    <span className="text-3xl font-bold">${stockData.price.toFixed(2)}</span>
                  )}
                  {stockData.changePercent != null && (
                    <span className={`flex items-center gap-1 text-base font-semibold ${
                      stockData.changePercent >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {stockData.changePercent >= 0 ? <TrendingUp size={16}/> : <TrendingDown size={16}/>}
                      {stockData.changePercent >= 0 ? '+' : ''}{stockData.changePercent.toFixed(2)}%
                    </span>
                  )}
                  {stockData.marketCap && (
                    <span className="text-sm text-white/60">Mkt Cap: {fmtLarge(stockData.marketCap)}</span>
                  )}
                  {stockData.beta !== undefined && (
                    <span className="text-sm text-white/60">β {fmt(stockData.beta)}</span>
                  )}
                </div>

                {/* Quick KPI strip */}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'P/E (TTM)', val: fmt(stockData.peRatio) },
                    { label: 'EPS (TTM)', val: stockData.earningsPerShare ? `$${fmt(stockData.earningsPerShare)}` : '—' },
                    { label: 'Div Yield', val: stockData.dividendYield ? fmtPct(stockData.dividendYield) : '—' },
                    { label: '52W Range', val: stockData.fiftyTwoWeekLow && stockData.fiftyTwoWeekHigh ? `$${stockData.fiftyTwoWeekLow.toFixed(0)} – $${stockData.fiftyTwoWeekHigh.toFixed(0)}` : '—' },
                  ].map(kpi => (
                    <div key={kpi.label} className="bg-white/10 rounded-lg px-3 py-2 border border-white/10">
                      <div className="text-[10px] text-white/50 uppercase tracking-wide mb-0.5">{kpi.label}</div>
                      <div className="text-sm font-bold text-white">{kpi.val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Website link */}
              {stockData.website && (
                <a href={stockData.website} target="_blank" rel="noopener noreferrer"
                  className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white/70 hover:text-white transition-colors">
                  <Globe size={18}/>
                </a>
              )}
            </div>
          </div>

          {/* Tab bar */}
          <div className="bg-white border-b border-slate-200 px-6 flex items-center gap-1">
            {(['overview', 'financials', 'analyst'] as Tab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
            <div className="ml-auto py-2">
              <WhatsAppSendButton
                label={`${stockData.ticker} Analysis`}
                content={[
                  `${stockData.ticker} — ${stockData.companyName}`,
                  stockData.price ? `Price: $${stockData.price.toFixed(2)}${stockData.changePercent != null ? ` (${stockData.changePercent >= 0 ? '+' : ''}${stockData.changePercent.toFixed(2)}%)` : ''}` : '',
                  stockData.sector ? `Sector: ${stockData.sector}` : '',
                  stockData.recommendationKey ? `Rating: ${stockData.recommendationKey.replace('_', ' ').toUpperCase()}` : '',
                  stockData.business_summary ? `\nProfile: ${stockData.business_summary.slice(0, 400)}...` : '',
                ].filter(Boolean).join('\n')}
              />
            </div>
          </div>

          {/* Tab content */}
          <div className="p-6 space-y-6">

            {/* ── OVERVIEW TAB ────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <>
                {/* Business summary */}
                {(() => {
                  // ETF fallback descriptions
                  const etfInfo: Record<string, string> = {
                    'XLE': 'Energy Select Sector SPDR Fund — tracks the Energy Select Sector Index, providing exposure to oil, gas, and energy equipment companies.',
                    'XLK': 'Technology Select Sector SPDR Fund — tracks the Technology Select Sector Index, providing exposure to IT services, software, and hardware companies.',
                    'XLF': 'Financial Select Sector SPDR Fund — tracks the Financial Select Sector Index, providing exposure to banks, insurance, and financial services.',
                    'XLV': 'Health Care Select Sector SPDR Fund — tracks the Health Care Select Sector Index, providing exposure to pharmaceuticals, biotech, and healthcare equipment.',
                    'XLI': 'Industrial Select Sector SPDR Fund — tracks the Industrial Select Sector Index, providing exposure to aerospace, defense, and industrial conglomerates.',
                    'XLU': 'Utilities Select Sector SPDR Fund — tracks the Utilities Select Sector Index, providing exposure to electric, gas, and water utilities.',
                    'XLY': 'Consumer Discretionary Select Sector SPDR Fund — tracks the Consumer Discretionary Select Sector Index.',
                    'XLB': 'Materials Select Sector SPDR Fund — tracks the Materials Select Sector Index.',
                    'XLC': 'Communication Services Select Sector SPDR Fund — tracks the Communication Services Select Sector Index.',
                    'XLRE': 'Real Estate Select Sector SPDR Fund — tracks the Real Estate Select Sector Index, providing exposure to REITs and real estate companies.',
                    'SPY': 'SPDR S&P 500 ETF Trust — tracks the S&P 500 Index, providing broad exposure to large-cap U.S. equities.',
                    'QQQ': 'Invesco QQQ Trust — tracks the Nasdaq-100 Index, providing exposure to the 100 largest non-financial Nasdaq-listed companies.',
                    'GLD': 'SPDR Gold Shares — tracks the price of gold bullion.',
                    'SLV': 'iShares Silver Trust — tracks the price of silver bullion.',
                    'USO': 'United States Oil Fund — tracks the price of WTI crude oil futures.',
                    'GDX': 'VanEck Gold Miners ETF — tracks gold mining companies.',
                    'XME': 'SPDR S&P Metals & Mining ETF — tracks metals and mining companies.',
                    'TLT': 'iShares 20+ Year Treasury Bond ETF — tracks long-term U.S. Treasury bonds.',
                    'HYG': 'iShares iBoxx $ High Yield Corporate Bond ETF — tracks high-yield corporate bonds.',
                    'IWM': 'iShares Russell 2000 ETF — tracks the Russell 2000 small-cap index.',
                  };
                  const rawSummary = stockData.business_summary;
                  const hasRealSummary = rawSummary && !rawSummary.toLowerCase().includes('no business summary') && rawSummary.length > 20;
                  const summary = hasRealSummary ? rawSummary : (etfInfo[stockData.ticker] || null);
                  if (!summary) return null;
                  return (
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <SectionHead icon={<Building2 size={16}/>} label={etfInfo[stockData.ticker] ? 'ETF Profile' : 'Company Profile'} />
                      <p className="text-sm text-slate-600 leading-relaxed">{summary}</p>
                      {stockData.industry && stockData.industry !== 'N/A' && (
                        <p className="mt-3 text-xs text-slate-500">
                          <span className="font-semibold">Industry:</span> {stockData.industry}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Price Chart with Bollinger Bands */}
                {chartData.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <SectionHead icon={<Activity size={16}/>} label="Price Chart" />
                      <div className="flex gap-1">
                        {['1M', '3M', '6M', '1Y'].map(p => (
                          <button
                            key={p}
                            onClick={() => { setChartPeriod(p); loadChartData(stockData.ticker, p); }}
                            className={`px-2 py-1 text-xs rounded ${chartPeriod === p ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-slate-400 hover:text-slate-600'}`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={['auto', 'auto']} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        {chartData[0]?.upper && (
                          <>
                            <Area type="monotone" dataKey="upper" stroke="none" fill="#e5e7eb" fillOpacity={0.3} />
                            <Area type="monotone" dataKey="lower" stroke="none" fill="#fff" fillOpacity={1} />
                            <Line type="monotone" dataKey="upper" stroke="#d1d5db" strokeWidth={1} strokeDasharray="4 4" dot={false} name="Upper BB" />
                            <Line type="monotone" dataKey="lower" stroke="#d1d5db" strokeWidth={1} strokeDasharray="4 4" dot={false} name="Lower BB" />
                          </>
                        )}
                        {chartData[0]?.sma20 && (
                          <Line type="monotone" dataKey="sma20" stroke="#94a3b8" strokeWidth={1} strokeDasharray="5 5" dot={false} name="SMA20" />
                        )}
                        <Line type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={2} dot={false} name="Price" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Peer Comparison */}
                {peers.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <SectionHead icon={<BarChart3 size={16}/>} label={`Sector Peers — ${stockData.sector || ''}`} />
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase">
                            <th className="text-left py-2 pr-4">Ticker</th>
                            <th className="text-left py-2 pr-4">Company</th>
                            <th className="text-right py-2 pr-4">Price</th>
                            <th className="text-right py-2 pr-4">Change</th>
                            <th className="text-right py-2 pr-4">P/E</th>
                            <th className="text-right py-2">Mkt Cap</th>
                          </tr>
                        </thead>
                        <tbody>
                          {peers.map((peer: any) => (
                            <tr key={peer.ticker} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                              onClick={() => { setSearchTicker(peer.ticker); loadStockData(peer.ticker); }}>
                              <td className="py-2 pr-4 font-mono font-semibold text-blue-600">{peer.ticker}</td>
                              <td className="py-2 pr-4 text-slate-700 truncate max-w-[150px]">{peer.companyName}</td>
                              <td className="py-2 pr-4 text-right">{peer.price ? `$${peer.price.toFixed(2)}` : '—'}</td>
                              <td className={`py-2 pr-4 text-right ${(peer.changePercent || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {peer.changePercent != null ? `${peer.changePercent >= 0 ? '+' : ''}${peer.changePercent.toFixed(2)}%` : '—'}
                              </td>
                              <td className="py-2 pr-4 text-right">{peer.peRatio ? peer.peRatio.toFixed(1) : '—'}</td>
                              <td className="py-2 text-right">{peer.marketCap ? `$${(peer.marketCap / 1e9).toFixed(1)}B` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent News */}
                {headlines.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <SectionHead icon={<Globe size={16}/>} label="Recent News" />
                    <div className="space-y-3">
                      {headlines.map((h: any, i: number) => (
                        <a key={i} href={h.url} target="_blank" rel="noopener noreferrer"
                          className="block group">
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <p className="text-sm text-slate-800 group-hover:text-blue-600 transition-colors leading-snug">
                                {h.title}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {h.source}{h.publishedAt ? ` · ${new Date(h.publishedAt).toLocaleDateString()}` : ''}
                              </p>
                            </div>
                            <ExternalLink size={14} className="text-slate-300 group-hover:text-blue-500 shrink-0 mt-1" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Insights */}
                {(stockData.bullish_points?.length || stockData.bearish_points?.length) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {stockData.bullish_points && stockData.bullish_points.length > 0 && (
                      <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden">
                        <div className="bg-emerald-50 px-5 py-3 border-b border-emerald-100 flex items-center gap-2">
                          <TrendingUp className="text-emerald-600" size={16}/>
                          <h4 className="font-bold text-sm text-emerald-800">Bullish Case</h4>
                        </div>
                        <ul className="p-5 space-y-2">
                          {stockData.bullish_points.map((pt, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                              <span className="text-emerald-500 mt-0.5 shrink-0">•</span>
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {stockData.bearish_points && stockData.bearish_points.length > 0 && (
                      <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                        <div className="bg-red-50 px-5 py-3 border-b border-red-100 flex items-center gap-2">
                          <TrendingDown className="text-red-600" size={16}/>
                          <h4 className="font-bold text-sm text-red-800">Bearish Case</h4>
                        </div>
                        <ul className="p-5 space-y-2">
                          {stockData.bearish_points.map((pt, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                              <span className="text-red-500 mt-0.5 shrink-0">•</span>
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Trading snapshot */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <SectionHead icon={<Activity size={16}/>} label="Price & Range" />
                    <StatRow label="Current Price" value={fmtPrice(stockData.price)} />
                    <StatRow label="Previous Close" value={fmtPrice(stockData.previousClose)} />
                    <StatRow label="Day Range" value={stockData.dayLow && stockData.dayHigh ? `$${stockData.dayLow.toFixed(2)} – $${stockData.dayHigh.toFixed(2)}` : '—'} />
                    <StatRow label="52-Week Low" value={fmtPrice(stockData.fiftyTwoWeekLow)} />
                    <StatRow label="52-Week High" value={fmtPrice(stockData.fiftyTwoWeekHigh)} />
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <SectionHead icon={<BarChart3 size={16}/>} label="Volume & Risk" />
                    <StatRow label="Volume" value={stockData.volume?.toLocaleString() ?? '—'} />
                    <StatRow label="Avg Volume (3M)" value={stockData.averageVolume?.toLocaleString() ?? '—'} />
                    <StatRow label="Beta" value={fmt(stockData.beta)} note="vs S&P 500" />
                    {stockData.dividendYield && <StatRow label="Dividend Yield" value={fmtPct(stockData.dividendYield)} />}
                    {stockData.dividendRate && <StatRow label="Div Rate / Share" value={`$${fmt(stockData.dividendRate)}`} />}
                  </div>
                </div>
              </>
            )}

            {/* ── FINANCIALS TAB ──────────────────────────────────── */}
            {activeTab === 'financials' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Valuation */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <SectionHead icon={<DollarSign size={16}/>} label="Valuation" />
                  <StatRow label="Market Cap" value={fmtLarge(stockData.marketCap)} />
                  <StatRow label="Enterprise Value" value={fmtLarge(stockData.enterpriseValue)} />
                  <StatRow label="P/E (TTM)" value={fmt(stockData.peRatio)} />
                  <StatRow label="Forward P/E" value={fmt(stockData.forwardPE)} />
                  <StatRow label="PEG Ratio" value={fmt(stockData.pegRatio)} />
                  <StatRow label="Price / Book" value={fmt(stockData.priceToBook)} />
                  <StatRow label="Price / Sales" value={fmt(stockData.priceToSales)} />
                  <StatRow label="EV / Revenue" value={fmt(stockData.evToRevenue)} />
                  <StatRow label="EV / EBITDA" value={fmt(stockData.evToEbitda)} />
                </div>

                {/* Profitability */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <SectionHead icon={<BarChart3 size={16}/>} label="Profitability" />
                  <StatRow label="Profit Margin" value={fmtPct(stockData.profitMargin)}
                    positive={stockData.profitMargin ? stockData.profitMargin > 0.1 : false}
                    negative={stockData.profitMargin ? stockData.profitMargin < 0 : false} />
                  <StatRow label="Operating Margin" value={fmtPct(stockData.operatingMargin)}
                    positive={stockData.operatingMargin ? stockData.operatingMargin > 0.15 : false} />
                  <StatRow label="Return on Assets" value={fmtPct(stockData.returnOnAssets)} />
                  <StatRow label="Return on Equity" value={fmtPct(stockData.returnOnEquity)}
                    positive={stockData.returnOnEquity ? stockData.returnOnEquity > 0.2 : false} />
                  <StatRow label="Revenue (TTM)" value={fmtLarge(stockData.totalRevenue)} />
                  <StatRow label="Revenue Growth" value={fmtPct(stockData.revenueGrowth)}
                    positive={stockData.revenueGrowth ? stockData.revenueGrowth > 0 : false}
                    negative={stockData.revenueGrowth ? stockData.revenueGrowth < 0 : false} />
                  <StatRow label="Earnings Growth" value={fmtPct(stockData.earningsGrowth)}
                    positive={stockData.earningsGrowth ? stockData.earningsGrowth > 0 : false}
                    negative={stockData.earningsGrowth ? stockData.earningsGrowth < 0 : false} />
                  <StatRow label="EPS (TTM)" value={stockData.earningsPerShare ? `$${fmt(stockData.earningsPerShare)}` : '—'} />
                </div>

                {/* Financial health */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <SectionHead icon={<Activity size={16}/>} label="Balance Sheet" />
                  <StatRow label="Total Cash" value={fmtLarge(stockData.totalCash)} />
                  <StatRow label="Total Debt" value={fmtLarge(stockData.totalDebt)} />
                  <StatRow label="Debt / Equity" value={fmt(stockData.debtToEquity)}
                    negative={stockData.debtToEquity ? stockData.debtToEquity > 2 : false} />
                  <StatRow label="Current Ratio" value={fmt(stockData.currentRatio)}
                    positive={stockData.currentRatio ? stockData.currentRatio > 1.5 : false}
                    negative={stockData.currentRatio ? stockData.currentRatio < 1 : false} />
                </div>

                {/* Dividends */}
                {(stockData.dividendYield || stockData.dividendRate) && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <SectionHead icon={<PieChart size={16}/>} label="Dividends" />
                    <StatRow label="Dividend Yield" value={fmtPct(stockData.dividendYield)} />
                    <StatRow label="Annual Div Rate" value={stockData.dividendRate ? `$${fmt(stockData.dividendRate)}` : '—'} />
                    <StatRow label="Payout Ratio" value={fmtPct(stockData.payoutRatio)} />
                  </div>
                )}
              </div>
            )}

            {/* ── ANALYST TAB ─────────────────────────────────────── */}
            {activeTab === 'analyst' && (
              <>
                {stockData.targetMeanPrice ? (
                  <div className="space-y-4">
                    {/* Rating card */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <SectionHead icon={<Target size={16}/>} label="Analyst Consensus" />
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <div className="text-xs text-slate-400 mb-1">Consensus Rating</div>
                          <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide ${ratingColor(stockData.recommendationKey)}`}>
                            {stockData.recommendationKey?.replace('_', ' ') || 'N/A'}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-400 mb-1">Analyst Count</div>
                          <div className="text-2xl font-bold text-slate-800">{stockData.numberOfAnalystOpinions ?? '—'}</div>
                        </div>
                      </div>

                      {/* Price targets */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {[
                          { label: 'Low Target', val: fmtPrice(stockData.targetLowPrice) },
                          { label: 'Mean Target', val: fmtPrice(stockData.targetMeanPrice), highlight: true },
                          { label: 'High Target', val: fmtPrice(stockData.targetHighPrice) },
                        ].map(t => (
                          <div key={t.label} className={`rounded-lg p-4 text-center border ${t.highlight ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="text-xs text-slate-500 mb-1">{t.label}</div>
                            <div className={`text-xl font-bold ${t.highlight ? 'text-blue-700' : 'text-slate-800'}`}>{t.val}</div>
                          </div>
                        ))}
                      </div>

                      {/* Upside bar */}
                      {upsidePct !== null && stockData.price && (
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Current ${stockData.price.toFixed(2)}</span>
                            <span className={`font-bold ${upsidePct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {upsidePct >= 0 ? '+' : ''}{upsidePct.toFixed(1)}% upside to target
                            </span>
                            <span>Target {fmtPrice(stockData.targetMeanPrice)}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            {upsidePct >= 0 ? (
                              <div
                                className="h-full bg-emerald-500 rounded-full"
                                style={{ width: `${Math.min(upsidePct, 100)}%` }}
                              />
                            ) : (
                              <div
                                className="h-full bg-red-400 rounded-full ml-auto"
                                style={{ width: `${Math.min(Math.abs(upsidePct), 100)}%` }}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
                    <Award size={40} className="mx-auto mb-3 opacity-20"/>
                    <p className="font-medium">No analyst data available for {stockData.ticker}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────── */}
      {!isLoading && !stockData && (
        <div className="p-12 text-center">
          <Search size={48} className="mx-auto text-slate-200 mb-4"/>
          <h2 className="text-xl font-bold text-slate-700 mb-2">Stock Analysis</h2>
          <p className="text-slate-500 mb-2">Enter a ticker symbol above to get AI-powered insights, valuations, and company details.</p>
          {watchlistTickers.length > 0 && (
            <p className="text-sm text-slate-400">or click a ticker from your watchlist above <ArrowRight size={13} className="inline"/></p>
          )}
        </div>
      )}
    </div>
  );
};

export default StockAnalysisView;
